/**
 * @file stock-label-print.ts
 * @description Групповая печать этикеток из журнала остатков — чистые правила
 *   (phase19.22, W17-9c; ui-design §2).
 *
 * Строка остатка — склад × партия, а этикетка печатается на ПАРТИЮ: одна
 * партия, лежащая на двух складах, дала бы две строки и дубль в печати
 * (сервер ответил бы `duplicate_lot`). Поэтому отметить можно только первую
 * строку партии в отборе — остальные подписаны. Количество по умолчанию —
 * остаток партии, развес — одна этикетка на тару (решение пользователя п.6).
 */

import type { LabelPrintDialogItem } from "../labels/label-print-types";
import { formatSkuName, type StockLevelDto } from "../../../lib/api-wms";
import { defaultLabelQty } from "../../../lib/label-sheets";

/** Строку можно напечатать: есть партия и остаток. */
export function isPrintable(level: StockLevelDto): boolean {
  return Boolean(level.lot) && Number(level.quantity) > 0;
}

/** id строк, которые первыми в отборе представляют свою партию. */
export function firstRowPerLot(rows: StockLevelDto[]): Set<string> {
  const seen = new Set<string>();
  const first = new Set<string>();
  for (const row of rows) {
    const lotId = row.lot?.id;
    if (!lotId || seen.has(lotId)) continue;
    seen.add(lotId);
    first.add(row.id);
  }
  return first;
}

/** Количество этикеток по умолчанию для строки остатка. */
export function stockDefaultLabelQty(level: StockLevelDto): number {
  return defaultLabelQty({ quantity: level.quantity, isMeasured: level.sku?.isMeasured });
}

/** Строки диалога печати из отмеченных остатков (уже без дублей партий). */
export function stockPrintItems(
  rows: StockLevelDto[],
  qtyOf: (level: StockLevelDto) => number,
): LabelPrintDialogItem[] {
  return rows
    .filter((level) => level.lot)
    .map((level) => ({
      skuId: level.skuId,
      skuName: formatSkuName(level.sku),
      skuCode: level.sku?.code ?? "",
      lotId: level.lot?.id ?? null,
      lotCode: level.lot?.code,
      batchNumber: level.lot?.batchNumber,
      expiresAt: level.lot?.expiresAt ?? null,
      defaultQty: qtyOf(level),
      isMeasured: level.sku?.isMeasured,
      template: level.sku?.effectiveLabelTemplate,
      labelStatus: level.lot?.labelStatus ?? null,
    }));
}

/** Сумма этикеток отмеченных строк — «Печать (212)». */
export function totalLabels(rows: StockLevelDto[], qtyOf: (level: StockLevelDto) => number): number {
  return rows.reduce((sum, level) => sum + qtyOf(level), 0);
}
